import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';

interface UsageMeterProps {
  used: number;
  cap: number | null;
  mode: 'trial' | 'active' | 'blocked';
  variant?: 'chip' | 'full';
}

/**
 * Monochrome usage meter reusing the F055 progressbar pattern
 * (bg-border track + inner fill). No new design tokens.
 *
 * cap=null → unlimited tier: shows only the count, no bar, no fraction.
 * variant='chip' → compact header display with responsive labels.
 * variant='full' → bar + caption line for the settings billing section.
 */
export function UsageMeter({ used, cap, mode, variant = 'full' }: UsageMeterProps) {
  const { t } = useTranslation();

  const pct = cap ? Math.min(100, Math.round((used / cap) * 100)) : 0;

  const fillClass = pct >= 100 ? 'bg-destructive' : pct >= 80 ? 'bg-warning' : 'bg-foreground/30';

  const ariaLabel = t('billing.meter.aria_label');

  // Uncapped variant (unlimited tier)
  if (cap === null) {
    if (variant === 'chip') {
      return (
        <span
          className="inline-flex items-center gap-1 text-xs text-muted-foreground"
          aria-label={ariaLabel}
        >
          <span data-testid="usage-chip-count" className="tabular-nums">
            {used.toLocaleString()}
          </span>
          <span className="hidden md:inline text-muted-foreground/60">msg</span>
        </span>
      );
    }
    // A trial has no DM cap (§14) — but "(ilimitado)" reads as a paid perk and
    // confuses trial users (F097/A10). Use an explicit free-trial label here.
    const uncappedLabel =
      mode === 'trial'
        ? t('billing.meter.trial_uncapped_label')
        : t('billing.meter.uncapped_label');
    return (
      <div className="space-y-1 text-xs text-muted-foreground">
        <span data-testid="usage-count" className="tabular-nums">
          {used.toLocaleString()}
        </span>
        <span className="ml-1 text-muted-foreground/60">{uncappedLabel}</span>
      </div>
    );
  }

  // Capped variant
  const trialBadge =
    mode === 'trial' ? (
      <span data-testid="usage-trial-badge" className="ml-1 text-muted-foreground/70">
        {t('billing.meter.trial_suffix')}
      </span>
    ) : null;

  if (variant === 'chip') {
    return (
      <span className="inline-flex items-center gap-1.5" aria-label={ariaLabel}>
        {/* Compact bar */}
        <span className="hidden md:flex w-16 h-1 bg-border rounded-full overflow-hidden">
          <span
            className={cn('h-full rounded-full transition-all duration-300', fillClass)}
            style={{ width: `${pct}%` }}
            role="progressbar"
            aria-valuenow={pct}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label={ariaLabel}
          />
        </span>
        {/* Mobile: number only */}
        <span data-testid="usage-chip-count" className="text-xs tabular-nums md:hidden">
          {used.toLocaleString()}
        </span>
        {/* Desktop: full used/cap */}
        <span
          data-testid="usage-chip-full"
          className="hidden md:inline text-xs tabular-nums text-muted-foreground"
        >
          {used.toLocaleString()}/{cap.toLocaleString()}
          {trialBadge}
        </span>
      </span>
    );
  }

  // full variant
  return (
    <div className="space-y-1.5" aria-label={ariaLabel}>
      <div className="h-1.5 bg-border rounded-full overflow-hidden">
        <div
          className={cn('h-full rounded-full transition-all duration-300', fillClass)}
          style={{ width: `${pct}%` }}
          role="progressbar"
          aria-valuenow={pct}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={ariaLabel}
        />
      </div>
      <p data-testid="usage-caption" className="text-xs text-muted-foreground tabular-nums">
        {used.toLocaleString()} / {cap.toLocaleString()}
        {trialBadge}
      </p>
    </div>
  );
}
